import React, { Fragment, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import ApplicationComponent from '../components/careers_v2/application_component';
import PositionsApi from '../services/positionsApi';
import PageHelmet from '../components/common/PageHelmet';

export default function ApplicationScreen() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [position, setPosition] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const positionId = searchParams.get('positionId');
        if (positionId) {
            fetchPosition(positionId);
        } else {
            setLoading(false);
        }
    }, [searchParams]);

    const fetchPosition = async (positionId) => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await PositionsApi.getPositionById(positionId);
            
            if (response.success) {
                setPosition(response.data);
            } else {
                setError('Failed to fetch position details');
            }
        } catch (err) {
            console.error('Error fetching position:', err);
            // Check if it's a 404 error (position not found or deactivated)
            if (err.message && err.message.includes('404')) {
                setError('This position is no longer available or has been removed.');
            } else {
                setError('Failed to load position details. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRetry = () => {
        const positionId = searchParams.get('positionId');
        if (positionId) {
            fetchPosition(positionId);
        }
    };

    const handleBackToCareers = () => {
        navigate('/careers');
    };

    // Dynamic meta data based on position
    const getMetaData = () => {
        if (position) {
            return {
                title: `Apply for ${position.title} - Rubaai Careers`,
                description: `Apply for the ${position.title} position at Rubaai. Join our team and help shape the future of digital payments. ${position.description || ''}`,
                keywords: `Rubaai careers, ${position.title}, job application, fintech jobs, digital payment careers, ${position.department || 'technology'} jobs`
            };
        }
        return {
            title: "Job Application - Rubaai Careers",
            description: "Apply for exciting career opportunities at Rubaai. Join our team and help revolutionize digital payments.",
            keywords: "Rubaai careers, job application, fintech jobs, digital payment careers, technology jobs"
        };
    };

    const metaData = getMetaData();

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    color: '#fff',
                    py: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <PageHelmet 
                    title="Loading Application - Rubaai Careers"
                    description="Loading job application details..."
                    keywords="Rubaai careers, job application, loading"
                />
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress sx={{ color: '#8ABEFC', mb: 2 }} />
                    <Typography sx={{ color: '#fff', fontFamily: '"Wix Madefor Display", sans-serif' }}>
                        Loading position details...
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    color: '#fff',
                    py: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <PageHelmet 
                    title="Application Error - Rubaai Careers"
                    description="Unable to load job application details."
                    keywords="Rubaai careers, application error, job application"
                />
                <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
                    <Alert 
                        severity="error" 
                        sx={{ 
                            backgroundColor: 'rgba(255, 107, 107, 0.1)', 
                            color: '#ff6b6b',
                            border: '1px solid #ff6b6b',
                            mb: 2
                        }}
                    >
                        {error}
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button
                            onClick={handleRetry}
                            variant="outlined"
                            sx={{
                                color: '#8ABEFC',
                                borderColor: '#8ABEFC',
                                '&:hover': {
                                    borderColor: '#8ABEFC',
                                    backgroundColor: 'rgba(138, 190, 252, 0.1)'
                                }
                            }}
                        >
                            Try Again
                        </Button>
                        <Button
                            onClick={handleBackToCareers}
                            variant="contained"
                            sx={{
                                backgroundColor: '#8ABEFC',
                                color: '#000',
                                '&:hover': {
                                    backgroundColor: '#7aaeec'
                                }
                            }}
                        >
                            Back to Careers
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Fragment>
            <PageHelmet 
                title={metaData.title}
                description={metaData.description}
                keywords={metaData.keywords}
            />
            <ApplicationComponent position={position} />
        </Fragment>
    );
}
